import React from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Collapse,
} from 'reactstrap';
import { Form } from '@availity/form';
import { Input, Select, Switch } from '../../lib/form-field';
import { FieldArray } from 'formik';
import '@availity/yup';
import formValidation from './formValidation';
import { parseFollowUpDelays, formatFollowUpDelays } from './followUpDelays';
import {
  formatQuietHours,
  parseQuietHours,
  hasNoQuietHours,
} from './followUpQuietHours';

import { aiChannels, aiOutcomes, aiModels } from '../../config/aiAgents';
import { ACTIVE_GRACE_MS } from '../../config/aiConversationWindow';
import {
  WHATSAPP_TEMPLATE_LANGUAGES,
  metaLanguageFromTag,
} from '../../config/whatsappTemplateLanguages';
import { actualCountry } from '../../selectors/country';
import { writeInCountryLanguage } from '../../helpers/countryLanguage';
import * as aiAgentsActions from '../../store/aiAgents/actions';
import { useSelector, useDispatch } from 'react-redux';
import { SubmitButton } from '../Common/Modal/modalActionButtons';
import { useTranslation } from 'react-i18next';

// Derived from the list instead of repeated, so the default cannot become a
// value the select no longer offers.
const DEFAULT_MODEL = aiModels[0].value;

// A follow-up with a threshold up to this many minutes goes out even inside
// the night band, because the person has just written. The engine ties it to
// the same constant (SHORT_FOLLOW_UP_MS = ACTIVE_GRACE_MS), so the help texts
// read it from there instead of repeating the number.
const SHORT_FOLLOW_UP_MINUTES = ACTIVE_GRACE_MS / (60 * 1000);

const FormAiAgent = (props) => {
  const editingAiAgent = props.aiAgent || {};
  const leadFields = useSelector((state) => state.LeadField.leadFields);
  const country = useSelector(actualCountry);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  // The agent speaks the language of its country: every text below that
  // reaches the lead, word for word or through the model, is written in it.
  const languageNote = writeInCountryLanguage(t, country?.languageTag);

  const initialValues = {
    name: '',
    channel: 'whatsapp',
    active: true,
    objective: '',
    tone: '',
    senderName: '',
    openingTemplateName: '',
    // The Meta code of the country's language, the same one the engine falls
    // back on. Still required: a template is approved in one language, which
    // is not necessarily the country's.
    openingTemplateLanguage: metaLanguageFromTag(country?.languageTag) || '',
    useDirectSend: false,
    openingMessage: '',
    collectFields: [],
    allowedOutcomes: [],
    handoffWindowMinutes: 45,
    maxTurns: 20,
    model: DEFAULT_MODEL,
    bookliTaskWebhookUrl: '',
    ...editingAiAgent,
    followUpDelays: formatFollowUpDelays(editingAiAgent.followUp?.delayMinutes),
    ...formatQuietHours(editingAiAgent.followUp),
  };

  const handleSubmit = (values) => {
    // id, createdAt, updatedAt and countryId come from the document being
    // edited and must NOT be rewritten: createdAt and updatedAt are
    // client-side Dates and would overwrite the serverTimestamps the saga
    // manages. collectFieldsCount is the list's own column
    // (pages/AiAgents/List.js), handed in with the row: spread back, it was
    // written on the document at every edit.
    const {
      followUpDelays,
      quietHoursStart,
      quietHoursEnd,
      id,
      createdAt,
      updatedAt,
      countryId,
      collectFieldsCount,
      ...aiAgentValues
    } = values;
    const delayMinutes = parseFollowUpDelays(followUpDelays);

    const aiAgent = {
      ...aiAgentValues,
      collectFields: Array.isArray(values.collectFields)
        ? values.collectFields
        : [],
      allowedOutcomes: Array.isArray(values.allowedOutcomes)
        ? values.allowedOutcomes
        : [],
      handoffWindowMinutes: Number(values.handoffWindowMinutes),
      maxTurns: Number(values.maxTurns),
      // Rewritten whole, so the band must travel with the delays: the engine
      // runs the default band on an agent whose followUp lacks it.
      followUp: {
        attempts: delayMinutes.length,
        delayMinutes,
        quietHours: parseQuietHours(values),
      },
    };

    dispatch(
      props.create
        ? aiAgentsActions.createAiAgent(aiAgent)
        : aiAgentsActions.updateAiAgent({ id: editingAiAgent.id, ...aiAgent }),
    );
    props.closeModal();
  };

  return (
    <Card>
      <CardBody>
        <Form
          initialValues={initialValues}
          validationSchema={formValidation}
          onSubmit={handleSubmit}
        >
          {({ isValid, values }) => (
            <>
              <Row>
                <Col md="5">
                  <Input type="text" name="name" label="Name" />
                </Col>
                <Col md="4">
                  <Select
                    name="channel"
                    id="channel"
                    label="Channel"
                    isMulti={false}
                    options={aiChannels}
                    readOnly={!props.create}
                    subText={t(
                      'Cannot be changed after creation: an agent belongs to its channel. Create a new agent for a different channel.',
                    )}
                  />
                </Col>
                <Col md="3">
                  <Switch
                    name="active"
                    label={t('Active?')}
                    defaultChecked={values.active}
                    subText={t(
                      'Only controls whether new leads are routed to this agent. Conversations already open run to their end: this is not an emergency stop.',
                    )}
                  />
                </Col>
              </Row>
              {!props.create && (
                <Row>
                  <Col md="12">
                    <small className="font-size-12 text-muted">
                      {t(
                        'Changes apply to the conversations that start from now on. The ones already open finish with the configuration they were born with.',
                      )}
                    </small>
                  </Col>
                </Row>
              )}

              <hr />
              <h5 className="font-size-14 mb-3">{t('Behaviour')}</h5>
              <Row>
                <Col md="12">
                  <Input
                    type="textarea"
                    name="objective"
                    label="Objective"
                    rows="5"
                    subText={`${t(
                      'Describe in plain language what the agent must achieve talking to the lead.',
                    )} ${languageNote}`}
                  />
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <Input
                    type="textarea"
                    name="tone"
                    label="Tone"
                    rows="3"
                    subText={`${t(
                      'How the agent should sound and what it must never say. Leave empty for a neutral, professional tone.',
                    )} ${languageNote}`}
                  />
                </Col>
              </Row>

              <Collapse isOpen={values.channel === 'whatsapp'}>
                <Row>
                  <Col md="5">
                    <Input
                      type="text"
                      name="senderName"
                      label="Sender name"
                      subText={t(
                        'The name the agent gives the lead. It fills the second variable of the opening template, and the agent keeps using it for the rest of the conversation.',
                      )}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md="4">
                    <Input
                      type="text"
                      name="openingTemplateName"
                      label="Opening template"
                      subText={t(
                        'Name of the Meta-approved template used to open the conversation. WhatsApp forbids free-form first messages.',
                      )}
                    />
                  </Col>
                  <Col md="3">
                    <Select
                      name="openingTemplateLanguage"
                      id="openingTemplateLanguage"
                      label="Template language"
                      isMulti={false}
                      isSearchable
                      options={WHATSAPP_TEMPLATE_LANGUAGES}
                      subText={t(
                        'The language the template was approved in on Meta. It starts from the language of the country.',
                      )}
                    />
                  </Col>
                  <Col md="4">
                    <Switch
                      name="useDirectSend"
                      label={t('Use Meta Direct Send?')}
                      defaultChecked={values.useDirectSend}
                      subText={t(
                        'Opens the conversation without a pre-approved template. Only for WhatsApp accounts Meta has enabled for it, and only for utility messages.',
                      )}
                    />
                  </Col>
                </Row>
                <Collapse isOpen={values.useDirectSend}>
                  <Row>
                    <Col md="11">
                      <Input
                        type="textarea"
                        name="openingMessage"
                        label="Opening message"
                        rows="3"
                        subText={`${t(
                          'The first message the lead receives. Utility content only: no offers or promotions, or Meta refuses the send.',
                        )} ${languageNote}`}
                      />
                    </Col>
                  </Row>
                </Collapse>
              </Collapse>

              <Collapse isOpen={values.channel === 'call'}>
                <Row>
                  <Col md="9">
                    <Input
                      type="text"
                      name="bookliTaskWebhookUrl"
                      label="Bookli task webhook URL"
                    />
                  </Col>
                </Row>
              </Collapse>

              <hr />
              <h5 className="font-size-14 mb-3">{t('Data to collect')}</h5>
              <Row>
                <Col md="12">
                  <FieldArray
                    name="collectFields"
                    render={(arrayHelpers) => {
                      const fields = values.collectFields;
                      return (
                        <FormGroup>
                          <Label htmlFor="collectFields">
                            {t('Lead fields the agent may write')}
                          </Label>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline btn-light ml-4"
                            onClick={() =>
                              arrayHelpers.push({
                                leadFieldId: '',
                                required: false,
                                overwrite: false,
                                hint: '',
                              })
                            }
                          >
                            <i className="bx bx-plus font-size-11 align-middle"></i>{' '}
                            {t('Add')}
                          </button>
                          {Array.isArray(fields) &&
                            fields.map((field, index) => (
                              <Row key={index} className="mb-2">
                                <Col md="4">
                                  <Select
                                    name={`collectFields.${index}.leadFieldId`}
                                    label="Field"
                                    isMulti={false}
                                    isSearchable
                                    addonType="append"
                                    options={leadFields.map(({ id, name }) => ({
                                      label: name,
                                      value: id,
                                    }))}
                                  >
                                    <button
                                      className="btn btn-danger p-0"
                                      style={{ width: '31px' }}
                                      type="button"
                                      onClick={() => arrayHelpers.remove(index)}
                                    >
                                      <i className="bx bx-trash d-block font-size-15" />
                                    </button>
                                  </Select>
                                </Col>
                                <Col md="4">
                                  <Input
                                    type="text"
                                    name={`collectFields.${index}.hint`}
                                    label="Hint"
                                  />
                                </Col>
                                <Col md="2">
                                  <Switch
                                    name={`collectFields.${index}.required`}
                                    label={t('Required?')}
                                    defaultChecked={fields[index]?.required}
                                  />
                                </Col>
                                <Col md="2">
                                  <Switch
                                    name={`collectFields.${index}.overwrite`}
                                    label={t('Overwrite?')}
                                    defaultChecked={fields[index]?.overwrite}
                                  />
                                </Col>
                              </Row>
                            ))}
                          <small className="font-size-12 text-muted">
                            {t(
                              'Overwrite lets the agent replace a value the lead already has. Off by default.',
                            )}
                          </small>
                        </FormGroup>
                      );
                    }}
                  />
                </Col>
              </Row>

              <hr />
              <h5 className="font-size-14 mb-3">{t('Allowed outcomes')}</h5>
              <Row>
                <Col md="8">
                  <Select
                    name="allowedOutcomes"
                    id="allowedOutcomes"
                    label="Outcomes"
                    isMulti
                    options={aiOutcomes}
                    subText={t(
                      'Monetization orders come from the acquisition order, not from the agent.',
                    )}
                  />
                </Col>
              </Row>

              <hr />
              <h5 className="font-size-14 mb-3">{t('Timing and engine')}</h5>
              <Row>
                <Col md="3">
                  <Input
                    type="number"
                    name="handoffWindowMinutes"
                    label="Handoff window (minutes)"
                    subText={t(
                      'Counted from the start of the conversation. Once it expires without an outcome, the lead goes to the call center queue.',
                    )}
                  />
                </Col>
                <Col md="3">
                  <Input
                    type="text"
                    name="followUpDelays"
                    label="Follow-up delays"
                    subText={t(
                      "Minutes of silence after the person's last message before the agent sends a follow-up, one per value (for example 5 and 10). Every reply of hers restarts the count, and the number of values is also the maximum number of follow-ups for the whole conversation. Each one must be shorter than the handoff window, or it can never fire. A threshold up to {{minutes}} minutes sends its follow-up at any hour, because she has just written; a longer one respects the protected night band, and a follow-up falling due inside it is cancelled.",
                      { minutes: SHORT_FOLLOW_UP_MINUTES },
                    )}
                  />
                </Col>
                <Col md="2">
                  <Input type="number" name="maxTurns" label="Max turns" />
                </Col>
                <Col md="4">
                  <Select
                    name="model"
                    id="model"
                    label="Model"
                    isMulti={false}
                    options={aiModels}
                  />
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <FormGroup>
                    <Label htmlFor="quietHoursStart">
                      {t('Protected night band')}
                    </Label>
                    <Row>
                      <Col md="2">
                        <Input
                          type="time"
                          name="quietHoursStart"
                          label="Band start"
                        />
                      </Col>
                      <Col md="2">
                        <Input
                          type="time"
                          name="quietHoursEnd"
                          label="Band end"
                        />
                      </Col>
                    </Row>
                    <small className="font-size-12 text-muted d-block">
                      {t(
                        "In this band the agent sends no follow-ups. A follow-up that would fall due here is cancelled, not held until the morning: it would arrive hours later, with a text written for a few minutes of silence. The exception are follow-ups with a threshold up to {{minutes}} minutes, which go out at any hour because the person has just written. The agent's replies to the person's messages do not depend on the band. Times are on the country's clock; equal start and end mean no band.",
                        { minutes: SHORT_FOLLOW_UP_MINUTES },
                      )}
                    </small>
                    {hasNoQuietHours(
                      values.quietHoursStart,
                      values.quietHoursEnd,
                    ) && (
                      <small className="font-size-12 text-info d-block mt-1">
                        {t('No band: follow-ups go out at any hour')}
                      </small>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <div className="modal-footer">
                <SubmitButton
                  disabled={!isValid || props.loading}
                  create={props.create}
                />
              </div>
            </>
          )}
        </Form>
      </CardBody>
    </Card>
  );
};

export default FormAiAgent;
